"use client";

import React, { useEffect, useRef, useState } from "react";
import { cn } from "~/lib/utils";

interface Heading {
  id: string;
  text: string;
  level: number;
}

interface TableOfContentsProps {
  className?: string;
  onNavigate?: () => void;
}

function buildHeadingTree(headings: Heading[]): Heading[] {
  if (headings.length === 0) return [];

  const tree: Heading[] = [];
  const stack: Heading[] = [];

  for (const heading of headings) {
    while (
      stack.length > 0 &&
      stack[stack.length - 1]!.level >= heading.level
    ) {
      stack.pop();
    }

    tree.push(heading);
    stack.push(heading);
  }

  return tree;
}

export function TableOfContents({
  className,
  onNavigate,
}: TableOfContentsProps) {
  const [headings, setHeadings] = useState<Heading[]>([]);
  const [activeId, setActiveId] = useState<string>("");
  const activeIdRef = useRef(activeId);
  activeIdRef.current = activeId;

  useEffect(() => {
    const articleElement = document.querySelector("[data-article-content]");
    if (!articleElement) return;

    const headingElements = articleElement.querySelectorAll(
      "h1, h2, h3, h4, h5, h6",
    );
    const headingsArray: Heading[] = [];

    headingElements.forEach((element) => {
      if (element.id) {
        headingsArray.push({
          id: element.id,
          text: element.textContent || "",
          level: parseInt(element.tagName.charAt(1)),
        });
      }
    });

    setHeadings(buildHeadingTree(headingsArray));
  }, []);

  useEffect(() => {
    if (headings.length === 0) return;

    const findActiveHeading = () => {
      const headingPositions = headings.map((heading) => {
        const element = document.getElementById(heading.id);
        return {
          id: heading.id,
          top: element ? element.getBoundingClientRect().top : Infinity,
        };
      });

      let active = headingPositions.find((h) => h.top >= -50 && h.top <= 100);

      if (!active) {
        const above = headingPositions
          .filter((h) => h.top < -50)
          .sort((a, b) => b.top - a.top);
        active = above[0];
      }

      if (!active) {
        const below = headingPositions
          .filter((h) => h.top > 100)
          .sort((a, b) => a.top - b.top);
        active = below[0];
      }

      if (active && active.id !== activeIdRef.current) {
        setActiveId(active.id);
      }
    };

    const observer = new IntersectionObserver(() => findActiveHeading(), {
      root: null,
      rootMargin: "-100px",
      threshold: 0,
    });

    headings.forEach(({ id }) => {
      const element = document.getElementById(id);
      if (element) observer.observe(element);
    });

    findActiveHeading();

    return () => observer.disconnect();
  }, [headings]);

  const handleClick = async (id: string) => {
    onNavigate?.();
    const url = `${window.location.origin}${window.location.pathname}#${id}`;

    window.history.pushState({}, "", `#${id}`);

    try {
      await navigator.clipboard.writeText(url);
    } catch (err) {
      console.error(err);
      const textArea = document.createElement("textarea");
      textArea.value = url;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);
    }

    const element = document.getElementById(id);
    if (element) {
      const offset = 80;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth",
      });
    }
  };

  if (headings.length === 0) return null;

  // Find the minimum level to use as baseline (usually h2 in blog posts)
  const minLevel = Math.min(...headings.map((h) => h.level));

  return (
    <div className={cn("space-y-3", className)}>
      <h4 className="text-foreground text-xs font-semibold tracking-wider uppercase">
        Sadržaj članka
      </h4>
      <nav>
        <ul className="space-y-2.5">
          {headings.map((heading) => {
            // Calculate indentation relative to minimum level
            const indent = (heading.level - minLevel) * 12; // 12px per level

            return (
              <li key={heading.id} style={{ paddingLeft: `${indent}px` }}>
                <button
                  onClick={() => handleClick(heading.id)}
                  className={cn(
                    "text-muted-foreground hover:text-foreground block w-full text-left text-sm leading-snug text-pretty transition-all",
                    {
                      "text-primary font-medium": activeId === heading.id,
                      "text-xs": heading.level - minLevel >= 2, // Smaller text for deeply nested
                    },
                  )}
                >
                  {heading.text}
                </button>
              </li>
            );
          })}
        </ul>
      </nav>
    </div>
  );
}
